"use client"

import { getAppStoreUrl, GOOGLE_PLAY_URL } from "@/lib/app-links"

import { accountText, type LocaleProps } from "../../personal-web"

/**
 * The orange-gradient hero at the top of the install flow. Promotes the
 * Yoho native app as the recommended one-tap install path; manual install
 * (QR / Manual / Tap-to-install) lives below.
 *
 * Used by both the account/esim/{id}/installation page (with a real eSIM
 * loaded) and the public /installation-guide page (no eSIM context) — the
 * hero content doesn't depend on eSIM data, so it's identical in both.
 */
export function Hero({ lng }: LocaleProps) {
  return (
    <section
      className="overflow-hidden rounded-3xl bg-gradient-to-br from-orange-500 via-orange-500 to-amber-500 p-6 text-white shadow-[0_24px_60px_-30px_rgba(234,88,12,0.7)] md:p-8"
      aria-labelledby="install-app-hero-title"
    >
      <h2
        id="install-app-hero-title"
        className="text-[26px] font-extrabold leading-[1.1] tracking-tight md:text-[34px]"
      >
        {accountText(lng, "install.heroTitle")}
      </h2>
      <p className="mt-3 max-w-xl text-[15px] leading-7 text-orange-50 md:text-base">
        {accountText(lng, "install.heroBody")}
      </p>
      <div className="mt-6 flex flex-wrap gap-3">
        <a
          href={getAppStoreUrl(lng)}
          target="_blank"
          rel="noreferrer noopener"
          className="group inline-flex min-h-12 items-center gap-3 rounded-2xl bg-stone-950 px-5 py-2.5 text-white shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)] transition hover:bg-stone-900"
          aria-label={accountText(lng, "install.downloadAppStore")}
        >
          <svg
            viewBox="0 0 384 512"
            className="h-6 w-6"
            fill="currentColor"
            aria-hidden
          >
            <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
          </svg>
          <span className="flex flex-col text-left">
            <span className="text-[10px] font-medium leading-none opacity-75">
              {accountText(lng, "install.downloadOnThe")}
            </span>
            <span className="text-[17px] font-bold leading-tight tracking-tight">
              App Store
            </span>
          </span>
        </a>
        <a
          href={GOOGLE_PLAY_URL}
          target="_blank"
          rel="noreferrer noopener"
          className="group inline-flex min-h-12 items-center gap-3 rounded-2xl bg-stone-950 px-5 py-2.5 text-white shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)] transition hover:bg-stone-900"
          aria-label={accountText(lng, "install.getItOnGooglePlay")}
        >
          <svg
            viewBox="0 0 512 512"
            className="h-6 w-6"
            fill="currentColor"
            aria-hidden
          >
            <path d="M325.3 234.3 104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
          </svg>
          <span className="flex flex-col text-left">
            <span className="text-[10px] font-medium leading-none opacity-75">
              {accountText(lng, "install.getItOn")}
            </span>
            <span className="text-[17px] font-bold leading-tight tracking-tight">
              Google Play
            </span>
          </span>
        </a>
      </div>
    </section>
  )
}
